import React from 'react';
import { CheckmarkCircleIcon, ClockIcon, InlineEditIcon, RemoveIcon } from '../../../../components/icons/icons';
import InlineEdit from '../../../../components/inlineEdit/inlineEdit';
import StatusCircle from '../../../../components/statusCircle/statusCircle';
import ToggleButton from '../../../../components/toggleButton/toggleButton';
import Tooltip from '../../../../components/tooltip/tooltip';
import { __formatDateMM_DD_YYYY_US, __formatTimeHH_MM_Meridian } from '../../../../utils/dateTimeUtils';
import { __assignStatusClass, __capitalizeFirstLetter, __roundToTwoDecimals } from '../../../../utils/utils';
import { CrawlfishJob, User } from '../../../crawler.interface';
import { _updateJob } from '../../../services/crawler.services';
import './jobDetailMetadata.scss';
import { outsideClick } from '../../../../utils/hooks';
import { InlineTypeAhead } from '../../../../components/inlineTypeAhead/inlineTypeAhead';
import UserContext, { UserContextInterface } from '../../../../contexts/user-context';
import Loader from '../../../../components/loader/loader';
import { JobStatus } from 'src/app/crawler/crawler.enums';

export interface JobDetailMetadataProps {
    job: CrawlfishJob;
    updateJob: (job: CrawlfishJob) => void;
    toggleReview: () => void;
}

function JobDetailMetadata({ job, updateJob, toggleReview }: JobDetailMetadataProps) {

    const [updatedJob, setUpdatedJob] = React.useState({
        isEditModeName: false,
        isEditModeOwner: false,
        name: '',
        owner: ''
    });
    const [isEditOwnerVisible, setEditOwnerVisible] = React.useState<boolean>(false);
    const [crawlfishUserNames, setCrawlfishUserNames] = React.useState<string[]>([]);
    const [isReviewed, setReviewed] = React.useState<boolean>(() => job.reviewed); // eslint-disable-line 
    const [isControlListExpanded, setControlListExpand] = React.useState<boolean>(false);
    const { user, crawlfishUsers } = React.useContext<UserContextInterface>(UserContext);
    const userCanChangeOwner = crawlfishUsers?.length > 0 ? true : false;

    const outerRef = React.useRef<HTMLLIElement>(null);
    outsideClick(outerRef, () => setEditOwnerVisible(false));

    React.useEffect(() => {
        init();
    }, [job.name, job.owner]);

    async function init() {
        setUpdatedJob({ ...updatedJob, name: job.name, owner: job.owner });
        const userNames = crawlfishUsers.map((user: User) => user.username);
        userNames.sort((a, b) => a.localeCompare(b));
        setCrawlfishUserNames(userNames);
    }

    function toggleNameEdit() {
        setUpdatedJob((currentState) => ({
            ...currentState,
            isEditModeName: !currentState.isEditModeName
        }));
    }

    function setJobOwnerValue(newOwner: string) {
        setUpdatedJob({ ...updatedJob, owner: newOwner });
    }

    function handleUpdateJob(e: React.ChangeEvent<HTMLInputElement>) {
        const key = e.target.name;
        const value = e.target.value;
        setUpdatedJob({ ...updatedJob, [key]: value });
    }

    async function saveUpdatedJob(inputName: string) {
        if (inputName === 'name') {
            job.name = updatedJob.name;
        } else if (inputName === 'owner') {
            job.owner = updatedJob.owner;
        }
        await _updateJob(job);
        setUpdatedJob(() => ({
            isEditModeName: false,
            isEditModeOwner: false,
            name: job.name,
            owner: job.owner
        }));
        updateJob(job);
        setEditOwnerVisible(false);
    }

    function handleReviewToggle() {
        setReviewed(prevState => !prevState);
        toggleReview();
    }

    const viewUsers = () => {
        if (userCanChangeOwner) {
            setEditOwnerVisible(true);
        }
    };

    return (
        <div className={"metadata-bar expanded"} data-testid="job-detail-metadata-bar">
            {job.id != 0 ?
                <ul className="metadata-list no-list-style" data-testid="metadata-header-list">

                    <li className="metadata-item job-id">
                        <div className="bold-label mini">JOB ID</div>
                        {job.id}
                    </li>

                    <li className="metadata-item job-name">
                        <div className="bold-label mini">JOB NAME</div>
                        <div className="flex-nowrap" data-testid="metadata-job-name">
                            {job?.name ? job?.name : "Unnamed"}
                            <a className="inline-edit edit-job-name" role="button" data-testid="edit-job-name" onClick={toggleNameEdit}>
                                <InlineEditIcon />
                            </a>
                        </div>
                        <InlineEdit inputValue={updatedJob.name || job.name} inputName="name" isVisible={updatedJob.isEditModeName} handleOnChange={handleUpdateJob} handleOnSave={saveUpdatedJob} handleOnClose={() => setUpdatedJob((currentState) => ({ ...currentState, isEditModeName: false }))}
                        />
                    </li>

                    <li className="metadata-item job-owner" ref={outerRef}>
                        {!isEditOwnerVisible ?
                            <a className={"inline-edit edit-job-owner edit-with-inline " + (userCanChangeOwner ? 'editable' : 'un-editable')} role="button" onClick={viewUsers} title={job.owner} data-testid="edit-job-owner">
                                <div className="bold-label mini">JOB OWNER</div>
                                <span className="job-owner-single">
                                    {job.owner || 'n/a'}
                                </span>
                                {userCanChangeOwner &&
                                    <InlineEditIcon />
                                }
                            </a>
                            :
                            <div className="edit-owner-box flex-nowrap">
                                <InlineTypeAhead options={crawlfishUserNames} setValue={setJobOwnerValue} alwaysShowOptions={true} />
                                <a className="save-edit" data-testid="save-job-owner-edit" onClick={() => saveUpdatedJob('owner')}>
                                    <CheckmarkCircleIcon />
                                </a>
                                <a className="remove-button" onClick={() => setEditOwnerVisible(false)} title="Cancel Edit" aria-disabled style={{ paddingBottom: "3px" }}>
                                    <RemoveIcon width={10} />
                                </a>
                            </div>
                        }
                    </li>

                    <li className="metadata-item domains">
                        <div className="bold-label mini"># OF DOMAINS</div>
                        {job.requested_uris?.length}
                    </li>
                    <li className="metadata-item status">
                        <div className="bold-label mini">STATUS</div>
                        <StatusCircle statusClass={__assignStatusClass(job.status)} />
                        {job?.status ? job.status : 'Not Active'}
                    </li>
                    <li className="metadata-item max-pages">
                        <div className="bold-label mini">MAX PAGES</div>
                        {job.rules?.max_page_count.toLocaleString()}
                    </li>
                    <li className="metadata-item created">
                        <div className="bold-label mini">CREATED</div>
                        {job.created_at ? __formatDateMM_DD_YYYY_US(job.created_at) : ""}

                        <Tooltip content={__formatTimeHH_MM_Meridian(job.created_at)} aria="job created time">
                            <ClockIcon />
                        </Tooltip>
                    </li>
                    <li className="metadata-item total-pages" data-testid="total-pages">
                        <div className="bold-label mini">TOTAL PAGES FOUND</div>
                        {job.info?.pages_complete?.toLocaleString()}
                    </li>
                    <li className="metadata-item queued-pages" data-testid="queued-pages">
                        <div className="bold-label mini">PAGES IN QUEUE</div>
                        {job.status === JobStatus.Complete ? '0' : job.info?.pages_queued?.toLocaleString()}
                    </li>
                    <li className="metadata-item reviewed">
                        <div className="bold-label mini">REVIEWED</div>
                        <ToggleButton handleClick={handleReviewToggle}
                            label={
                                job.reviewed ?
                                    <label className="italicized">Reviewed</label>
                                    :
                                    <label>Review</label>
                            }
                            isOn={!job.reviewed} />
                    </li>
                    <li className="metadata-item results">
                        <div className="bold-label mini">RESULTS</div>
                        <a className="inline-edit results-link" role="button" rel="noopener noreferrer" href={`/domain-status/${job.id}`} target="_blank" data-testid="domain-status-page-link">
                            View
                            <InlineEditIcon />
                        </a>
                    </li>
                    <li className="metadata-item ai-typing">
                        <div className="bold-label mini">AI TYPED</div>
                        {job?.rules?.ai_typing ? <span>True</span> : <span>False</span>}
                    </li>
                    <li className="metadata-item include-footer">
                        <div className="bold-label mini">Includes Footer</div>
                        {job?.rules?.footer_keywords ? <span>True</span> : <span>False</span>}
                    </li>
                    {
                        (job?.rules?.ai_typing) ?
                            <li className="metadata-item ai-cost">
                                <div className="bold-label mini">AI COST</div>
                                <span>${__roundToTwoDecimals(job?.info?.cost?.ai || 0)}</span>
                            </li>
                            : null
                    }
                    {
                        job?.rules?.path_control ?
                            <>
                                <li className="metadata-item control-type">
                                    <div className="bold-label mini">PATH CONTROL</div>
                                    {__capitalizeFirstLetter(job?.rules?.path_control)}
                                </li>
                                <li className="metadata-item control-list">
                                    <div className="bold-label mini">
                                        PATH CONTROL LIST
                                        {(job?.rules?.control_paths && job?.rules?.control_paths?.length > 2) ?
                                            <button onClick={() => setControlListExpand((prevState) => !prevState)} className={"path-control-list-expand " + (isControlListExpanded ? "expanded" : "")} title="toggle path control list"></button>
                                            : null
                                        }
                                    </div>
                                    <div className="path-control-list-wrapper">
                                        {(job?.rules?.control_paths && job?.rules?.control_paths?.length > 0) ?
                                            <ul className={"path-control-list " + (isControlListExpanded ? "expanded" : "")}>
                                                {job?.rules?.control_paths?.map((path: string) => <li key={path}>{path}</li>)}
                                            </ul>
                                            :
                                            <i style={{ fontSize: "12px" }}>No paths provided</i>
                                        }
                                    </div>
                                </li>
                            </>
                            : null
                    }
                </ul>
                :
                <Loader loadingText="Loading Metadata ..." spinnerColor="#000" />
            }
        </div >
    );
}

export default React.memo(JobDetailMetadata);